import type { SearchOptions } from "../types";

export interface ParsedSearchResult {
  /** The remaining search text after filters are extracted */
  search: string;
  /** Parsed filters */
  filters: Record<string, unknown>;
}

/**
 * Parses a search string and extracts special filter formats
 * Supported formats:
 * - column=value (exact match)
 * - column="quoted value" (exact match with quoted values)
 * - column>value (greater than)
 * - column>=value (greater than or equal)
 * - column<value (less than)
 * - column<=value (less than or equal)
 * - column!=value (not equal)
 * - column~value (like/contains)
 */
export function parseSearchFilters(searchText: string): ParsedSearchResult {
  if (!searchText?.trim()) {
    return { search: "", filters: {} };
  }

  const filters: Record<string, unknown> = {};
  let remainingText = searchText;

  // Define regex patterns for different filter formats
  const filterPatterns = [
    // Match: column="quoted value" or column='quoted value'
    {
      regex: /(\w+)\s*=\s*["']([^"']*?)["']/g,
      operator: "eq",
    },
    // Match: column>=value
    {
      regex: /(\w+)\s*>=\s*([^\s]+)/g,
      operator: "gte",
    },
    // Match: column<=value
    {
      regex: /(\w+)\s*<=\s*([^\s]+)/g,
      operator: "lte",
    },
    // Match: column!=value
    {
      regex: /(\w+)\s*!=\s*([^\s]+)/g,
      operator: "ne",
    },
    // Match: column>value (must come after >=)
    {
      regex: /(\w+)\s*>\s*([^\s]+)/g,
      operator: "gt",
    },
    // Match: column<value (must come after <=)
    {
      regex: /(\w+)\s*<\s*([^\s]+)/g,
      operator: "lt",
    },
    // Match: column~value (like/contains)
    {
      regex: /(\w+)\s*~\s*([^\s]+)/g,
      operator: "like",
    },
    // Match: column=value (unquoted, must come last to not interfere with other operators)
    {
      regex: /(\w+)\s*=\s*([^\s]+)/g,
      operator: "eq",
    },
  ];

  // Process each filter pattern
  for (const pattern of filterPatterns) {
    let match: RegExpExecArray | null;
    const regex = new RegExp(pattern.regex.source, pattern.regex.flags);

    while ((match = regex.exec(remainingText)) !== null) {
      const [fullMatch, column, value] = match;

      // Convert value to appropriate type
      const processedValue = processFilterValue(value, pattern.operator);

      // Handle multiple filters for the same column (e.g., range filters)
      const newFilter = {
        operator: pattern.operator,
        value: processedValue,
      };

      if (filters[column]) {
        // If we already have a filter for this column, convert to array or merge ranges
        const existingFilter = filters[column] as
          | { operator: string; value: unknown }
          | { operator: string; value: unknown }[];

        if (Array.isArray(existingFilter)) {
          // Already an array, append
          existingFilter.push(newFilter);
        } else {
          // Convert single filter to array
          filters[column] = [existingFilter, newFilter];
        }
      } else {
        // First filter for this column
        filters[column] = newFilter;
      }

      // Remove the matched filter from the remaining text
      remainingText = remainingText.replace(fullMatch, "");
    }
  }

  // Clean up remaining text (remove extra spaces, normalize)
  const cleanSearch = remainingText.replace(/\s+/g, " ").trim();

  return {
    search: cleanSearch,
    filters,
  };
}

/**
 * Process and convert filter values to appropriate types
 */
function processFilterValue(value: string, operator: string): unknown {
  // Handle quoted values (remove quotes)
  if (
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'"))
  ) {
    return value.slice(1, -1);
  }

  // Handle boolean values
  if (value.toLowerCase() === "true") return true;
  if (value.toLowerCase() === "false") return false;
  if (value.toLowerCase() === "null") return null;

  // Handle date conversion for comparison operators
  if (["gt", "gte", "lt", "lte"].includes(operator)) {
    // Check for various date formats and convert to milliseconds
    const dateValue = convertDateToMilliseconds(value);
    if (dateValue !== null) {
      return dateValue;
    }

    // Handle numeric values for comparison operators
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      return numValue;
    }
  }

  // Handle timestamp values (if it looks like a timestamp)
  if (/^\d{10,13}$/.test(value)) {
    return parseInt(value, 10);
  }

  // Return as string for other cases
  return value;
}

/**
 * Convert various date formats to milliseconds
 * Supports: YYYY-MM-DD, YYYY-MM-DDTHH:mm:ss, YYYY-MM-DDTHH:mm:ssZ, etc.
 */
function convertDateToMilliseconds(value: string): number | null {
  // Check for ISO date formats
  const isoDateRegex =
    /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:\d{2})?)?$/;

  if (isoDateRegex.test(value)) {
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date.getTime();
    }
  }

  // Check for simple date format YYYY-MM-DD
  const simpleDateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (simpleDateRegex.test(value)) {
    const date = new Date(value + "T00:00:00.000Z");
    if (!isNaN(date.getTime())) {
      return date.getTime();
    }
  }

  return null;
}

/**
 * Merges parsed filters with existing SearchOptions
 */
export function mergeSearchFilters(
  options: SearchOptions,
  parsedResult: ParsedSearchResult
): SearchOptions {
  return {
    ...options,
    search: parsedResult.search || undefined,
    filters: {
      ...options.filters,
      ...parsedResult.filters,
    },
  };
}

/**
 * Helper function to parse search and return updated SearchOptions
 */
export function parseAndMergeSearchOptions(
  searchText: string,
  existingOptions: SearchOptions = {}
): SearchOptions {
  const parsed = parseSearchFilters(searchText);
  return mergeSearchFilters(existingOptions, parsed);
}
